import React from "react";
import { cockpit } from "@/api";
import {
  applyChangedEvent,
  domainTick,
  emptyCounters,
  liveScopeKey,
  reconnectDelayMs,
  type ConsoleDomain,
  type ConsoleLiveEvent,
  type DomainCounters,
  type LiveScope,
} from "./live-updates";

/**
 * One subscription per plane for the whole console (#544).
 *
 * Screens no longer poll: they read the counters of the domains they render and refetch when one
 * moves. The provider owns the connection, the bounded reconnect, and the scope identity, so a
 * tenant switch cannot leave a screen rendering the previous business's rows.
 */
export interface LiveUpdatesValue {
  tenant: DomainCounters;
  owner: DomainCounters;
  /** Changes when the session or browsed business changes; data from another key is not reusable. */
  scopeKey: string;
  /**
   * Whether this scope's streams have delivered their opening event, or have been waited for long
   * enough (#2266). A load that starts after it reads everything that event stands for, so the
   * console starts its pages' loads then instead of loading them twice.
   */
  ready: boolean;
}

const INITIAL: LiveUpdatesValue = {
  tenant: emptyCounters(),
  owner: emptyCounters(),
  scopeKey: liveScopeKey(null, null),
  ready: true,
};

/** How long a page waits for the stream before it loads anyway; the opening event then refetches.
 * A stream that fails first ends the wait at once. */
export const LIVE_READY_LIMIT_MS = 1_000;

export const LiveUpdatesContext = React.createContext<LiveUpdatesValue>(INITIAL);

type Subscribe = (handlers: {
  onEvent: (event: ConsoleLiveEvent) => void;
  onError: () => void;
}) => () => void;

/**
 * Keeps one stream alive: reconnects with 1→30 s exponential backoff after a drop. The server
 * opens every connection with a full invalidation, so reconnecting is also the catch-up — no page
 * reload, no separate replay protocol.
 *
 * The backoff resets only once a connection has proved itself, not on its opening event: a stream
 * that is torn down right after that event (a proxy that flushes and closes, a crash-looping API)
 * would otherwise reconnect every second forever, and each reconnect makes every screen refetch.
 * A healthy connection proves itself with its 15-second heartbeat.
 *
 * A console brought back to the front (#2274) trusts a stream that kept talking: it has already
 * delivered every change. One silent for longer than two heartbeats cannot vouch for the time away
 * (a sleeping laptop, a suspended mobile tab, a dropped network), so it is reopened at once instead
 * of after its backoff, and its opening event refreshes every screen in one wave.
 */
const HEALTHY_AFTER_MS = 5_000;
const QUIET_AFTER_MS = 35_000;

function useStream(
  enabled: boolean,
  scope: LiveScope,
  subscribe: Subscribe,
  onCounters: (update: (counters: DomainCounters) => DomainCounters) => void,
  onOpened: () => void,
  onFailed: () => void,
): void {
  const scopeRef = React.useRef(scope);
  scopeRef.current = scope;

  React.useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let attempt = 0;
    let generation = 0;
    let stop: (() => void) | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let heardAt = 0;

    const open = () => {
      if (closed) return;
      const mine = ++generation;
      const openedAt = Date.now();
      heardAt = openedAt;
      const closer = subscribe({
        onEvent: (event) => {
          if (closed || mine !== generation) return;
          heardAt = Date.now();
          if (Date.now() - openedAt >= HEALTHY_AFTER_MS) attempt = 0;
          if (event.type !== "console.changed") return;
          onCounters((counters) => applyChangedEvent(counters, event, scopeRef.current).counters);
          // The opening event is the first change event of a connection. Applied in the same batch
          // as its counters, so nothing reads "ready" without the moves it stands for.
          onOpened();
        },
        onError: () => {
          if (closed || mine !== generation) return;
          onFailed();
          generation += 1;
          stop?.();
          stop = null;
          attempt += 1;
          timer = setTimeout(open, reconnectDelayMs(attempt));
        },
      });
      // A subscribe implementation that fails synchronously has already moved the generation on;
      // close its stream here rather than leaving it running behind the retry.
      if (mine !== generation) closer();
      else stop = closer;
    };

    // Both events fire on a return to the tab; reopening counts as hearing, so only one reopens.
    const onFront = () => {
      if (closed || document.visibilityState !== "visible" || Date.now() - heardAt <= QUIET_AFTER_MS) return;
      if (timer) clearTimeout(timer);
      timer = null;
      stop?.();
      stop = null;
      open();
    };

    open();
    window.addEventListener("focus", onFront);
    document.addEventListener("visibilitychange", onFront);
    return () => {
      closed = true;
      window.removeEventListener("focus", onFront);
      document.removeEventListener("visibilitychange", onFront);
      if (timer) clearTimeout(timer);
      stop?.();
    };
    // `scope` travels through a ref so the event handler always reads the current one; the scope
    // fields are still dependencies because a switch must reopen the stream under the new context.
  }, [enabled, subscribe, onCounters, onOpened, onFailed, scope.scope, scope.scopeId]);
}

export interface LiveUpdatesProviderProps {
  userId: string | null;
  /**
   * The tenant the session actually acts in: its own for an operator or admin, the browsed one for
   * an owner. It is what the server stamps on every tenant event, so it is what the scope guard
   * compares against — and an owner without a browsed business has no tenant stream to open.
   */
  tenantId: string | null;
  /** Owners additionally read the cross-tenant plane that the Businesses screens render. */
  owner: boolean;
  children: React.ReactNode;
}

export function LiveUpdatesProvider({
  userId,
  tenantId,
  owner,
  children,
}: LiveUpdatesProviderProps) {
  const scopeKey = liveScopeKey(userId, tenantId);
  const [tenantCounters, setTenantCounters] = React.useState<DomainCounters>(emptyCounters);
  const [ownerCounters, setOwnerCounters] = React.useState<DomainCounters>(emptyCounters);

  // A new scope starts from zero: counters from the previous business must never be read as
  // "nothing changed" for the new one.
  React.useEffect(() => {
    setTenantCounters(emptyCounters());
    setOwnerCounters(emptyCounters());
  }, [scopeKey]);

  const tenantScope = React.useMemo<LiveScope>(
    () => ({ scope: "tenant", scopeId: tenantId }),
    [tenantId],
  );
  const ownerScope = React.useMemo<LiveScope>(() => ({ scope: "owner_global", scopeId: null }), []);

  const subscribeTenant = React.useCallback<Subscribe>(
    (handlers) => cockpit.subscribeConsoleEvents(handlers),
    [],
  );
  const subscribeOwner = React.useCallback<Subscribe>(
    (handlers) => cockpit.subscribeOwnerEvents(handlers),
    [],
  );

  // A tenant stream opens per scope, so its opening counts for the scope it was opened in; a scope
  // change closes it before another event can arrive. The owner plane is one stream for the whole
  // session and stays open across a business switch.
  const scopeKeyRef = React.useRef(scopeKey);
  scopeKeyRef.current = scopeKey;
  const [tenantOpenedFor, setTenantOpenedFor] = React.useState<string | null>(null);
  const [ownerOpenedFor, setOwnerOpenedFor] = React.useState<string | null>(null);
  const [waitedFor, setWaitedFor] = React.useState<string | null>(null);
  const markTenantOpened = React.useCallback(() => setTenantOpenedFor(scopeKeyRef.current), []);
  const markOwnerOpened = React.useCallback(() => setOwnerOpenedFor(userId), [userId]);
  // A stream that fails before it opens retries after a backoff; pages do not wait for that.
  const stopWaiting = React.useCallback(() => setWaitedFor(scopeKeyRef.current), []);
  React.useEffect(() => {
    const timer = setTimeout(() => setWaitedFor(scopeKey), LIVE_READY_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [scopeKey]);

  const tenantEnabled = !!userId && !!tenantId;
  const ownerEnabled = !!userId && owner;
  useStream(tenantEnabled, tenantScope, subscribeTenant, setTenantCounters, markTenantOpened, stopWaiting);
  useStream(ownerEnabled, ownerScope, subscribeOwner, setOwnerCounters, markOwnerOpened, stopWaiting);
  const ready = waitedFor === scopeKey
    || ((!tenantEnabled || tenantOpenedFor === scopeKey) && (!ownerEnabled || ownerOpenedFor === userId));

  const value = React.useMemo<LiveUpdatesValue>(
    () => ({ tenant: tenantCounters, owner: ownerCounters, scopeKey, ready }),
    [tenantCounters, ownerCounters, scopeKey, ready],
  );

  return <LiveUpdatesContext.Provider value={value}>{children}</LiveUpdatesContext.Provider>;
}

/** Tick for the tenant-plane domains a screen renders; it changes when one of them moves. */
export function useLiveDomains(...domains: ConsoleDomain[]): number {
  const { tenant } = React.useContext(LiveUpdatesContext);
  return domainTick(tenant, domains);
}

/** Tick for the owner-global plane (Businesses and its sub-screens). */
export function useOwnerLiveDomains(...domains: ConsoleDomain[]): number {
  const { owner } = React.useContext(LiveUpdatesContext);
  return domainTick(owner, domains);
}

/** Identity of the current session + browsed business; refetch and drop state when it changes. */
export function useLiveScopeKey(): string {
  return React.useContext(LiveUpdatesContext).scopeKey;
}

/** Whether a load started now reads everything the streams' opening events stand for (#2266). */
export function useLiveReady(): boolean {
  return React.useContext(LiveUpdatesContext).ready;
}

/**
 * How many times these domains moved since the caller mounted (or since the scope changed). A
 * screen loads once on mount and refetches on this, so it never fires a duplicate load for changes
 * that happened before it existed.
 */
export function useLiveInvalidation(...domains: ConsoleDomain[]): number {
  return useInvalidationSince(useLiveDomains(...domains));
}

/** The owner-plane counterpart. */
export function useOwnerLiveInvalidation(...domains: ConsoleDomain[]): number {
  return useInvalidationSince(useOwnerLiveDomains(...domains));
}

/**
 * For a caller mounted before the streams are ready that makes its first load once they are: moves
 * up to readiness are what that load reads, so only later ones count.
 */
export function useLiveInvalidationSinceReady(...domains: ConsoleDomain[]): number {
  return useInvalidationSince(useLiveDomains(...domains), useLiveReady());
}

function useInvalidationSince(tick: number, ready = true): number {
  const scopeKey = useLiveScopeKey();
  const baseline = React.useRef({ key: scopeKey, tick, ready });
  // Re-baseline on a scope change, and also whenever the counter moved backwards — that is what a
  // scope reset looks like from here, and reading it as "nothing moved" would go permanently deaf.
  // Until the caller's first ready render nothing counts either.
  if (baseline.current.key !== scopeKey || tick < baseline.current.tick || !baseline.current.ready) {
    baseline.current = { key: scopeKey, tick, ready };
  }
  return tick - baseline.current.tick;
}
